import { useCallback, useEffect, useRef, useState } from 'react'

export interface MediaDevice {
  deviceId: string
  kind: MediaDeviceKind
  label: string
  groupId: string
}

const useWebcam = (
  videoRef: React.RefObject<HTMLVideoElement | null>,
  needsAudio: boolean = false,
) => {
  const streamRef = useRef<MediaStream | null>(null)
  const originalStreamRef = useRef<MediaStream | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const [devices, setDevices] = useState<MediaDevice[]>([])
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [isActive, setIsActive] = useState(true)
  const [hasWebCamPermission, setHasWebCamPermission] = useState<
    boolean | null
  >(null)
  const [hasMicrophonePermission, setHasMicrophonePermission] = useState<
    boolean | null
  >(null)

  const [isRecording, setIsRecording] = useState(false)
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null)

  const getDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const videoInputs = devices.filter(device => device.kind === 'videoinput')
      setDevices(videoInputs)
      if (videoInputs.every(d => !d.label)) {
        await navigator.mediaDevices
          .getUserMedia({ video: true, audio: false })
          .then(s => s.getTracks().forEach(t => t.stop())) // tidy up
      }
      return videoInputs
    } catch (err) {
      console.error('Could not enumerate devices:', err)
    }
  }, [])

  const connectToWebcam = useCallback(
    async (deviceId?: string) => {
      if (!videoRef?.current) return null
      const devices = await getDevices()
      const defaultCam = devices?.[0]

      streamRef.current?.getTracks().forEach(t => t.stop())

      try {
        const connectionParams: MediaStreamConstraints = {
          video: {
            width: 1200,
            height: 720,
            facingMode: 'user',
          },
          audio: needsAudio,
        }
        if (deviceId || defaultCam?.deviceId) {
          connectionParams.video = {
            ...(typeof connectionParams.video === 'object' &&
            connectionParams.video !== null
              ? connectionParams.video
              : {}),
            deviceId: { exact: deviceId || defaultCam?.deviceId },
          }
        }

        const stream =
          await navigator.mediaDevices.getUserMedia(connectionParams)
        streamRef.current = stream
        originalStreamRef.current = stream // Store the original video-only stream

        videoRef.current.srcObject = stream
        await videoRef.current.play()
        setReady(true)
        return stream
      } catch (err: unknown) {
        console.error('Error accessing webcam:', err)
        setError(err)

        // Check if the error is due to permission denial
        if (err instanceof Error) {
          if (
            err.name === 'NotAllowedError' ||
            err.name === 'PermissionDeniedError'
          ) {
            setHasWebCamPermission(false)
            // If audio is needed, also set microphone permission to false
            if (needsAudio) {
              setHasMicrophonePermission(false)
            }
          }
        }

        return null
      }
    },
    [needsAudio],
  )

  useEffect(() => {
    const controller = new AbortController()

    connectToWebcam().catch(err => {
      console.error('Error connecting to webcam:', err)
      setError(err)

      // Check if the error is due to permission denial
      if (err instanceof Error) {
        if (
          err.name === 'NotAllowedError' ||
          err.name === 'PermissionDeniedError'
        ) {
          setHasWebCamPermission(false)
        }
      }
    })

    return () => {
      streamRef.current?.getTracks().forEach(t => t.stop())
      controller.abort()
    }
  }, [connectToWebcam])

  const takePhoto = useCallback(async (): Promise<{
    blob: Blob
    dataUrl: string
  } | null> => {
    if (!videoRef?.current) return null

    const canvas = document.createElement('canvas')
    canvas.width = videoRef.current.videoWidth
    canvas.height = videoRef.current.videoHeight

    const ctx = canvas.getContext('2d')
    if (!ctx) return null

    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height)

    const blob = await new Promise<Blob>(res =>
      canvas.toBlob(b => res(b as Blob), 'image/jpeg', 0.9),
    )
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9)

    return { blob, dataUrl }
  }, [])

  const handleCamSelect = async (deviceId: string) => {
    await connectToWebcam(deviceId) // open the chosen camera
  }
  const startCamera = useCallback(async () => {
    try {
      // Check camera permission before starting
      const cameraPermissionStatus = await navigator.permissions.query({
        name: 'camera' as PermissionName,
      })

      if (cameraPermissionStatus.state === 'denied') {
        setHasWebCamPermission(false)
        setError(new Error('Camera permission denied'))
        return
      }

      if (cameraPermissionStatus.state === 'granted') {
        setHasWebCamPermission(true)
      }

      // Check microphone permission if audio is needed
      if (needsAudio) {
        try {
          const micPermissionStatus = await navigator.permissions.query({
            name: 'microphone' as PermissionName,
          })

          if (micPermissionStatus.state === 'denied') {
            setHasMicrophonePermission(false)
            setError(new Error('Microphone permission denied'))
            return
          }

          if (micPermissionStatus.state === 'granted') {
            setHasMicrophonePermission(true)
          }
        } catch (permErr) {
          console.error('Error checking microphone permission:', permErr)
          // Continue anyway - fallback for browsers that don't support permissions API
        }
      }

      // If state is 'prompt', permission will be requested when getUserMedia is called

      if (!streamRef.current) {
        const stream = await connectToWebcam()
        if (stream) {
          setHasWebCamPermission(true)
          if (needsAudio) {
            setHasMicrophonePermission(true)
          }
        } else {
          setHasWebCamPermission(false)
          if (needsAudio) {
            setHasMicrophonePermission(false)
          }
        }
      } else if (videoRef?.current) {
        videoRef.current.srcObject = streamRef.current
        await videoRef.current.play()
        setReady(true)
        setHasWebCamPermission(true)
        if (needsAudio) {
          setHasMicrophonePermission(true)
        }
      }
      setIsActive(true)
    } catch (err) {
      console.error('Error checking camera permission:', err)
      // Fallback: try to connect anyway
      if (!streamRef.current) {
        const stream = await connectToWebcam()
        if (stream) {
          setHasWebCamPermission(true)
        } else {
          setHasWebCamPermission(false)
        }
      }
    }
  }, [connectToWebcam])

  const stopCamera = useCallback(() => {
    if (videoRef?.current) {
      videoRef.current.pause()
      videoRef.current.srcObject = null
    }
    setReady(false)
    setIsActive(false)
  }, [])

  const startVideoRecording = useCallback(
    (onVideoRecorded?: (blob: Blob) => void) => {
      try {
        // use existing stream (already has audio if needsAudio=true)
        const recordingStream = streamRef.current
        if (!recordingStream) {
          throw new Error('No stream available for recording')
        }

        const mediaRecorder = new MediaRecorder(recordingStream)
        mediaRecorderRef.current = mediaRecorder

        const chunks: Blob[] = []
        mediaRecorder.ondataavailable = event => {
          if (event.data.size > 0) {
            chunks.push(event.data)
          }
        }

        mediaRecorder.onstop = () => {
          // check: ensure we have data before creating blob
          if (chunks.length === 0) {
            console.error('No recording data available - chunks array is empty')
            onVideoRecorded?.(new Blob([], { type: 'video/webm' }))
            return
          }

          const blob = new Blob(chunks, { type: 'video/webm' })

          // check: ensure blob has content
          if (blob.size === 0) {
            console.error('Recording blob is empty (0 bytes)')
            onVideoRecorded?.(blob)
            return
          }

          console.log(`Recording completed: ${blob.size} bytes`)
          setVideoBlob(blob)
          onVideoRecorded?.(blob)
        }

        // recording with 1 sec timeslice to ensure data is available
        mediaRecorder.start(1000)
        setIsRecording(true)
      } catch (error) {
        console.error('Error starting video recording:', error)
        setError(error)
      }
    },
    [videoRef],
  )

  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop()
        track.enabled = false
      })
      streamRef.current = null
    }
    setReady(false)
  }, [])

  const stopVideoRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      // check if MediaRecorder is in a state that allows requestData()
      if (
        mediaRecorderRef.current.state === 'recording' ||
        mediaRecorderRef.current.state === 'paused'
      ) {
        // request any remaining data before stopping
        mediaRecorderRef.current.requestData()
      }

      // small delay to ensure final part is processed, Zhenya issue
      setTimeout(() => {
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state !== 'inactive'
        ) {
          mediaRecorderRef.current.stop()
        }
        setIsRecording(false)
      }, 100)
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null
    }

    cleanupStream()
  }, [isRecording, cleanupStream])

  const stopRecordingOnly = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      // check if MediaRecorder is in a state that allows stopping
      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop()
        mediaRecorderRef.current = null
      }
      setIsRecording(false)
    }
  }, [isRecording])

  const disconnectWebcam = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = null
      videoRef.current.load() // Full cleanup of video element
    }

    cleanupStream()
  }, [cleanupStream])

  return {
    ready,
    error,
    devices,
    isActive,
    isRecording,
    videoBlob,
    hasWebCamPermission,
    hasMicrophonePermission,
    takePhoto,
    handleCamSelect,
    startCamera,
    stopCamera,
    startVideoRecording,
    stopVideoRecording,
    stopRecordingOnly,
    disconnectWebcam,
  }
}

export { useWebcam }
